import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import Icon from '../Icon/Icon';
import { fetchAdminCreations } from '../../Reducers/actions/creationsActions';
import { GENRES, GENRE_LABELS, GENRE_ICONS } from '../../constants/genres';
import { formatEurFromCents } from '../../../helpers/money';
import './CreationTable.scss';

const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All Categories' },
  ...Object.values(GENRES).map((slug) => ({ value: slug, label: GENRE_LABELS[slug] || slug })),
];

const ROWS_PER_PAGE = 10;
const COLUMNS = [
  { key: 'title', label: 'Creation', sortKey: 'title' },
  { key: 'creator_username', label: 'Creator username', sortKey: 'creator_username' },
  { key: 'type', label: 'Type', sortKey: 'type' },
  { key: 'category', label: 'Category', sortKey: 'category' },
  { key: 'monetisation', label: 'Monetisation', sortKey: 'monetisation' },
  { key: 'earnings', label: 'Earnings', sortKey: 'earnings' },
  { key: 'created_at', label: 'Created at', sortKey: 'created_at' },
  { key: 'updated_at', label: 'Updated at', sortKey: 'updated_at' },
];

function CreationTable({ onTotalCountChange }) {
  const navigate = useNavigate();
  const [creations, setCreations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const latestRequestRef = useRef(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [monetizationFilter, setMonetizationFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [culturalAreaFilter, setCulturalAreaFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showEditTableModal, setShowEditTableModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterDraft, setFilterDraft] = useState({
    monetization: 'all',
    type: 'all',
    category: 'all',
    cultural_area: 'all',
  });
  const [columnVisibility, setColumnVisibility] = useState(
    COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
  );
  const [searchDebounce, setSearchDebounce] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounce(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const fetchCreations = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    const isStale = () => requestId !== latestRequestRef.current;
    setLoading(true);
    setLoadError(null);
    try {
      const response = await fetchAdminCreations({
        page: currentPage,
        limit: ROWS_PER_PAGE,
        search: searchDebounce || undefined,
        monetization: monetizationFilter !== 'all' ? monetizationFilter : undefined,
        type: typeFilter !== 'all' ? typeFilter : undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        cultural_area: culturalAreaFilter !== 'all' ? culturalAreaFilter : undefined,
        sort_by: sortConfig.key || undefined,
        sort_dir: sortConfig.key ? sortConfig.direction : undefined,
      });
      if (isStale()) return;
      if (response?.success && response?.data) {
        const data = response.data;
        const list = Array.isArray(data) ? data : (data.creations || data.items || []);
        const totalCount = typeof data.total === 'number' ? data.total : list.length;
        setCreations(list);
        setTotal(totalCount);
        onTotalCountChange?.(totalCount);
      } else {
        throw new Error(response?.message || 'Failed to load creations');
      }
    } catch {
      if (isStale()) return;
      // Surface the failure instead of rendering it as "0 of 0 / No creations found".
      setCreations([]);
      setTotal(0);
      setLoadError('Could not load creations. Please try again.');
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [
    currentPage,
    searchDebounce,
    monetizationFilter,
    typeFilter,
    categoryFilter,
    culturalAreaFilter,
    sortConfig,
    onTotalCountChange,
  ]);

  useEffect(() => {
    fetchCreations();
  }, [fetchCreations]);

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    setCurrentPage(1);
    fetchCreations();
  };

  const handleMonetizationFilter = (value) => {
    setMonetizationFilter(value);
    setCurrentPage(1);
  };

  const handleOpenFilterModal = () => {
    setFilterDraft({
      monetization: monetizationFilter,
      type: typeFilter,
      category: categoryFilter,
      cultural_area: culturalAreaFilter,
    });
    setShowFilterModal(true);
  };

  const handleSaveFilterModal = () => {
    setMonetizationFilter(filterDraft.monetization);
    setTypeFilter(filterDraft.type);
    setCategoryFilter(filterDraft.category);
    setCulturalAreaFilter(filterDraft.cultural_area);
    setCurrentPage(1);
    setShowFilterModal(false);
  };

  const handleCancelFilterModal = () => {
    setShowFilterModal(false);
  };

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(Math.max(1, Math.min(pageNumber, totalPages)));
  };

  const totalPages = Math.max(1, Math.ceil(total / ROWS_PER_PAGE));
  const indexOfFirst = (currentPage - 1) * ROWS_PER_PAGE;
  const indexOfLast = Math.min(indexOfFirst + ROWS_PER_PAGE, total);

  const getSortIcon = (key) => (
    <Icon
      alias="arrow-up-down"
      width={16}
      height={16}
      color={sortConfig.key !== key ? '#0A0F1E' : '#FF3232'}
    />
  );

  const getCategoryIcon = (category) => {
    const slug = (category || '').toLowerCase();
    const icon = GENRE_ICONS[slug] || 'library-music';
    const label = GENRE_LABELS[slug] || category || '—';
    return { icon, label };
  };

  const getMonetisationLabel = (row) => {
    const paid = row.monetisation === 'paid' || row.monetization === 'paid' || row.is_paid;
    return paid ? 'Paid' : 'Free';
  };

  if (loading && creations.length === 0) {
    return (
      <div className="creation-table hyk-table">
        <div className="creation-table-loading">Loading creations…</div>
      </div>
    );
  }

  return (
    <div className="creation-table hyk-table">
      <div className="table-header">
        <div className="table-header-left">
          <form className="search-box" onSubmit={handleSearchSubmit}>
            <input
              type="text"
              placeholder="Search for creations"
              value={searchQuery}
              onChange={handleSearch}
              aria-label="Search creations"
            />
            {searchQuery && !loadError && (
              <span className="search-count" style={{ right: '40px' }}>
                {total} results
              </span>
            )}
            <Icon
              style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}
              alias="search"
              width={20}
              height={20}
              color="#0A0F1E"
            />
          </form>
          <div className="filter-buttons-group">
            <div className="filter-group">
              <button
                type="button"
                className={`filter-btn ${monetizationFilter === 'all' ? 'active' : ''}`}
                onClick={() => handleMonetizationFilter('all')}
                aria-pressed={monetizationFilter === 'all'}
              >
                All
              </button>
              <button
                type="button"
                className={`filter-btn ${monetizationFilter === 'free' ? 'active' : ''}`}
                onClick={() => handleMonetizationFilter('free')}
                aria-pressed={monetizationFilter === 'free'}
              >
                Free
              </button>
              <button
                type="button"
                className={`filter-btn ${monetizationFilter === 'paid' ? 'active' : ''}`}
                onClick={() => handleMonetizationFilter('paid')}
                aria-pressed={monetizationFilter === 'paid'}
              >
                Paid
              </button>
            </div>
            <button
              type="button"
              className="header-action-btn more-filters"
              onClick={handleOpenFilterModal}
              aria-label="Filter creations"
            >
              <Icon alias="three_dots" width={16} height={16} color="#0A0F1E" />
            </button>
          </div>
        </div>
        <div className="table-header-right">
          <div className="header-pagination">
            <button
              type="button"
              className="pagination-arrow"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              <Icon alias="chevron-left" width={16} height={16} color={currentPage === 1 ? '#ccc' : '#0A0F1E'} />
            </button>
            <span className="pagination-info">
              {loadError ? '—' : total === 0 ? '0 of 0' : `${indexOfFirst + 1}-${indexOfLast} of ${total}`}
            </span>
            <button
              type="button"
              className="pagination-arrow"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              aria-label="Next page"
            >
              <Icon alias="chevron-right" width={16} height={16} color={currentPage >= totalPages ? '#ccc' : '#0A0F1E'} />
            </button>
          </div>
          <div className="header-actions">
            <button
              type="button"
              className="header-action-btn"
              onClick={() => setShowEditTableModal(true)}
              aria-label="Edit table"
            >
              <Icon alias="plus" width={16} height={16} color="#0A0F1E" />
              <span>Edit table</span>
            </button>
            <button type="button" className="header-action-btn" aria-label="Export">
              <span>Export</span>
              <Icon alias="chevron-down" width={16} height={16} color="#0A0F1E" />
            </button>
          </div>
        </div>
      </div>

      {loadError ? (
        <div className="creation-table-error" role="alert">
          <span>{loadError}</span>
          <button type="button" className="creation-table-retry" onClick={fetchCreations}>
            Retry
          </button>
        </div>
      ) : creations.length === 0 ? (
        <div className="creation-table-empty">No creations found.</div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                {COLUMNS.filter((c) => columnVisibility[c.key]).map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.sortKey)}
                    className="sortable"
                  >
                    <div className="d-flex f-row aic jcsb">
                      {col.label}
                      {getSortIcon(col.sortKey)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {creations.map((row, index) => {
                const creationId = row.creation_id || row.id;
                const creatorId = row.creator_id || row.creator?.user_id;
                const creatorUsername = row.creator_username ?? row.creator?.userName ?? '';
                const displayUsername = creatorUsername.startsWith('@') ? creatorUsername : `@${creatorUsername}`;
                const title = row.title || row.creation_title || '—';
                const type = row.type || row.creation_type || '—';
                const category = row.category || '';
                const { icon: categoryIcon, label: categoryLabel } = getCategoryIcon(category);
                const monetisationLabel = getMonetisationLabel(row);
                const isPaid = monetisationLabel === 'Paid';

                return (
                  <tr key={creationId || index}>
                    {columnVisibility.title && (
                      <td>
                        <button
                          type="button"
                          className="creation-link"
                          onClick={() => navigate(`/admin/creation/${creationId}`)}
                        >
                          {title}
                        </button>
                      </td>
                    )}
                    {columnVisibility.creator_username && (
                      <td>
                        {creatorId ? (
                          <button
                            type="button"
                            className="creation-link"
                            onClick={() => navigate(`/admin/users/${creatorId}`)}
                          >
                            {displayUsername || '—'}
                          </button>
                        ) : (
                          <span>{displayUsername || '—'}</span>
                        )}
                      </td>
                    )}
                    {columnVisibility.type && (
                      <td>
                        <span className="creation-type">{type}</span>
                      </td>
                    )}
                    {columnVisibility.category && (
                      <td>
                        <span className="category-cell">
                          <Icon alias={categoryIcon} width={16} height={16} color="#0A0F1E" />
                          {categoryLabel}
                        </span>
                      </td>
                    )}
                    {columnVisibility.monetisation && (
                      <td>
                        <span className={`monetisation-pill ${isPaid ? 'paid' : 'free'}`}>
                          {monetisationLabel}
                        </span>
                      </td>
                    )}
                    {columnVisibility.earnings && (
                      <td>{formatEurFromCents(row.earnings)}</td>
                    )}
                    {columnVisibility.created_at && (
                      <td>{row.created_at ? moment(row.created_at).format('DD/MM/YYYY') : '—'}</td>
                    )}
                    {columnVisibility.updated_at && (
                      <td>{row.updated_at ? moment(row.updated_at).format('DD/MM/YYYY') : '—'}</td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showFilterModal && (
        <div className="filter-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="filter-creations-title">
          <div className="filter-modal">
            <div className="filter-modal-header">
              <h2 id="filter-creations-title">Filter creations</h2>
              <button
                type="button"
                className="filter-modal-close"
                onClick={handleCancelFilterModal}
                aria-label="Close"
              >
                <Icon alias="cross" width={20} height={20} color="#0A0F1E" />
              </button>
            </div>
            <div className="filter-modal-body">
              <div className="filter-modal-section">
                <span className="filter-modal-label">Monetisation</span>
                <div className="filter-modal-pills">
                  {['all', 'free', 'paid'].map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={`filter-pill ${filterDraft.monetization === value ? 'active' : ''}`}
                      onClick={() => setFilterDraft((d) => ({ ...d, monetization: value }))}
                    >
                      {value === 'all' ? 'All' : value === 'free' ? 'Free' : 'Paid'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="filter-modal-section">
                <span className="filter-modal-label">Type</span>
                <div className="filter-modal-pills">
                  {['all', 'digital', 'physical'].map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={`filter-pill ${filterDraft.type === value ? 'active' : ''}`}
                      onClick={() => setFilterDraft((d) => ({ ...d, type: value }))}
                    >
                      {value === 'all' ? 'All' : value === 'digital' ? 'Digital' : 'Physical'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="filter-modal-section">
                <span className="filter-modal-label">Category</span>
                <select
                  className="filter-modal-select"
                  value={filterDraft.category}
                  onChange={(e) => setFilterDraft((d) => ({ ...d, category: e.target.value }))}
                  aria-label="Category"
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-modal-section">
                <span className="filter-modal-label">Cultural Area</span>
                <select
                  className="filter-modal-select"
                  value={filterDraft.cultural_area}
                  onChange={(e) => setFilterDraft((d) => ({ ...d, cultural_area: e.target.value }))}
                  aria-label="Cultural Area"
                >
                  <option value="all">All Areas</option>
                </select>
              </div>
            </div>
            <div className="filter-modal-actions">
              <button type="button" className="filter-modal-save" onClick={handleSaveFilterModal}>
                SAVE CHANGES
              </button>
              <button type="button" className="filter-modal-cancel" onClick={handleCancelFilterModal}>
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditTableModal && (
        <div className="edit-table-modal-overlay" role="dialog" aria-modal="true" aria-label="Edit table columns">
          <div className="edit-table-modal">
            <div className="edit-table-header">
              <h2>Edit table</h2>
              <button
                type="button"
                className="close-btn"
                onClick={() => setShowEditTableModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="edit-table-content">
              <div className="edit-table-section">
                <span className="section-label">Columns</span>
                <div className="columns-list">
                  {COLUMNS.map((col) => (
                    <label key={col.key} className="column-checkbox">
                      <input
                        type="checkbox"
                        checked={columnVisibility[col.key]}
                        onChange={(e) =>
                          setColumnVisibility((prev) => ({ ...prev, [col.key]: e.target.checked }))
                        }
                      />
                      <span className="checkbox-custom" />
                      <span className="checkbox-label">{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="edit-table-actions">
              <button type="button" className="cancel-btn" onClick={() => setShowEditTableModal(false)}>
                Cancel
              </button>
              <button type="button" className="save-btn" onClick={() => setShowEditTableModal(false)}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreationTable;
